import { Typography } from '@mui/material';
import { useHealthInsuranceContext } from 'contexts/HealthInsuranceContext';
import { useApplicationCalculate } from 'hooks/useApplicationCalculate';
import {
  Body,
  Icon,
  MaterialAccordion,
  UserCircleIcon,
  useQuestionsContext
} from '@portal/ui-library';
import { useApplicationHelpers } from 'hooks/useApplicationHelpers';
import { PersonRoleEnum, TariffCalculationResult } from 'models/extension-generated';
import { useTranslation } from 'react-i18next';
import { formatPrice } from 'utils/string';
import { PartnerSummary } from './PartnerSummary';
import { useState, useRef, SyntheticEvent, useLayoutEffect, useEffect } from 'react';

export const SidebarContent = ({ isMobile = false }: { isMobile?: boolean }) => {
  const { t } = useTranslation('wizardSidebar');
  const { state } = useHealthInsuranceContext();
  const { questions } = useQuestionsContext();

  const { data, error } = useApplicationCalculate(state.model, questions);
  const { getPartnersByRole } = useApplicationHelpers(state.model);

  function getPartnerPrice(id: string): number {
    const partner = data?.partners.find(({ partnerId }) => partnerId === id);

    if (error) {
      return 0;
    }

    if (partner && partner.calculationMonthly) {
      return partner.calculationMonthly.totalAmount || 0;
    }

    return 0;
  }

  function getPartnerTariffs(id: string): Array<TariffCalculationResult> {
    const partner = data?.partners.find(({ partnerId }) => partnerId === id);
    if (partner && partner.tariffCalculations) {
      return partner.tariffCalculations;
    }

    return [];
  }

  const insuredPartners = getPartnersByRole(PersonRoleEnum.INSURED_PERSON);
  const overallSum = data?.calculationMonthly?.amount
    ? formatPrice(data?.calculationMonthly?.amount || 0)
    : '-,--';

  return (
    <>
      <div
        id={isMobile ? 'sidebar-mobile' : 'sidebar'}
        tabIndex={-1}
        aria-label={t('calculation.heading')}
        className="hidden m:block text-right mb-s w-full focus:outline-none focus:ring-2 focus:ring-highlight-80 focus:ring-offset-2 rounded-md"
      >
        <Typography component={'div'} variant="bodyMDBold" className="my-[0px]">
          {overallSum}
        </Typography>
        <div>
          <Typography variant="bodyXSBold">{t('calculation.frequency.monthly')}</Typography>
        </div>
        <div>
          <Typography
            component={'div'}
            variant="bodyMDRegular"
            className={'flex items-center justify-end text-text-60'}
          >
            {t('calculation.insuredPerson.count', { count: insuredPartners.length })}
            <span className="text-navigation-80 ml-[10px] flex items-center">
              {insuredPartners.map((partner) => (
                <Icon key={`icon-${partner.id}`} icon={<UserCircleIcon />} className="ml-[4px]" />
              ))}
            </span>
          </Typography>
        </div>
      </div>

      <div className="w-full">
        {insuredPartners.map((partner) => (
          <PartnerSummary
            isMobile={isMobile}
            key={partner.id}
            partner={partner}
            price={getPartnerPrice(partner.id!)}
            tariffs={getPartnerTariffs(partner.id!)}
            isLoading={false}
          />
        ))}
      </div>
    </>
  );
};

export const Sidebar = () => {
  const { t } = useTranslation('wizardSidebar');
  const { state } = useHealthInsuranceContext();
  const { questions } = useQuestionsContext();
  const { data, error } = useApplicationCalculate(state.model, questions);
  const { getPartnersByRole } = useApplicationHelpers(state.model);
  const insuredPartners = getPartnersByRole(PersonRoleEnum.INSURED_PERSON);
  const overallSum = error ? '-,--' : formatPrice(data?.calculationMonthly?.amount || 0);

  // State for controlling accordion expansion
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const accordionRef = useRef<HTMLDivElement>(null);

  // Event listener for sidebar-mobile focus
  // useEffect(() => {
  //   const handleSidebarMobileFocus = () => {
  //     setIsAccordionExpanded(true);
  //   };

  //   const sidebarMobileElement = document.getElementById('sidebar-mobile');
  //   if (sidebarMobileElement) {
  //     sidebarMobileElement.addEventListener('focus', handleSidebarMobileFocus);
  //   }

  //   return () => {
  //     if (sidebarMobileElement) {
  //       sidebarMobileElement.removeEventListener('focus', handleSidebarMobileFocus);
  //     }
  //   };
  // }, []);

  const renderMobileHeader = () => {
    return (
      <div className="flex justify-between w-full">
        <div className="mr-s">
          <Typography variant="bodyMDRegular" className={'text-white-100 flex items-center'}>
            {t('calculation.insuredPerson.count', { count: insuredPartners.length })}
            <span className="text-white-100 ml-[10px] flex items-center ">
              {insuredPartners.map((partner) => (
                <Icon key={`icon-${partner.id}`} icon={<UserCircleIcon />} className="ml-[4px]" />
              ))}
            </span>
          </Typography>
        </div>

        <div
          className="flex focus:outline-none focus:ring-2 focus:ring-highlight-80 focus:ring-offset-2 rounded-md"
          id="sidebar-mobile-price"
          tabIndex={0}
          role="region"
          aria-label={t('calculation.heading')}
        >
          <div>
            <Body small className="text-white-100" bold>
              {overallSum}
            </Body>
          </div>
          <div>
            <Body className="text-white-100 mr-m">&nbsp;{t('calculation.frequency.monthly')}</Body>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="hidden m:block p-l">
        <SidebarContent />
      </div>
      {/* Added accordionWrapper */}
      <div className="flex flex-col w-full s:w-auto m:hidden" ref={accordionRef}>
        <MaterialAccordion
          accordionProps={{
            expanded: isAccordionExpanded,
            onChange: (event: SyntheticEvent, expanded: boolean) =>
              setIsAccordionExpanded(expanded),
            sx: {
              backgroundColor: '#00635f',
              border: 'none'
            }
          }}
          summaryProps={{
            id: 'sidebar-mobile-header',
            sx: {
              backgroundColor: '#00635f'
            }
          }}
          accordionDetailsProps={{
            sx: {
              backgroundColor: '#00635f',
              padding: '0',
              paddingTop: '10px'
            }
          }}
          header={renderMobileHeader()}
        >
          <SidebarContent isMobile />
        </MaterialAccordion>
      </div>
    </>
  );
};
